import { useCallback, useEffect, useRef, useState } from 'react'
import { t } from '../utils/format'
import './HeroDiaporama.css'

/**
 * Le diaporama de l'ouverture.
 *
 * Il montre les photographies d'ABBUSINESS : le magasin, une realisation
 * chez un client, une livraison. Pas de photo de banque d'images, pas de
 * rendu de synthese, pas de photo de produit non plus : celles-la ont leur
 * place dans le catalogue, et la mise en avant produit les affiche deja
 * plus bas.
 *
 * Le composant ne connait pas ses images : elles lui sont passees. Il se
 * teste donc avec zero, une ou plusieurs photos sans toucher au manifeste
 * ni deposer de fichier factice dans public/.
 *
 * Trois comportements, et chacun repond a un etat reel du projet :
 *
 *   - aucune photo : rien n'est rendu. L'ouverture passe en pleine largeur.
 *     C'est l'etat du jour, et il vaut mieux qu'un cadre vide ;
 *   - une seule photo : l'image, sans pastilles ni defilement. Un carrousel
 *     d'un element n'est pas un carrousel, c'est une image avec des boutons
 *     qui ne font rien ;
 *   - plusieurs : fondu enchaine toutes les six secondes, pastilles de
 *     navigation, arret au survol et au clavier.
 *
 * Le defilement automatique s'arrete si le visiteur a demande moins
 * d'animations dans son systeme. Une image qui change toute seule pendant
 * qu'on lit la legende n'est pas une preference de style.
 */

const INTERVALLE = 6000

function preferenceMoinsDAnimations() {
  if (typeof window === 'undefined' || ! window.matchMedia) return false

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default function HeroDiaporama({ photos = [], lang = 'fr' }) {
  const [courante, setCourante] = useState(0)
  const [enPause, setEnPause] = useState(false)
  const minuterie = useRef(null)

  const total = photos.length
  const defile = total > 1 && ! enPause && ! preferenceMoinsDAnimations()

  const aller = useCallback((i) => setCourante(((i % total) + total) % total), [total])

  useEffect(() => {
    if (! defile) return undefined

    minuterie.current = setInterval(() => setCourante((c) => (c + 1) % total), INTERVALLE)

    return () => clearInterval(minuterie.current)
  }, [defile, total])

  /*
   | Une photo retiree du manifeste pendant que le diaporama tourne
   | laisserait l'index au-dela de la liste, et la legende disparaitrait
   | sans que l'image change.
   */
  useEffect(() => {
    if (courante >= total) setCourante(0)
  }, [courante, total])

  if (total === 0) return null

  const photo = photos[Math.min(courante, total - 1)]

  return (
    <figure
      className="hero-diaporama"
      onMouseEnter={() => setEnPause(true)}
      onMouseLeave={() => setEnPause(false)}
      onFocusCapture={() => setEnPause(true)}
      onBlurCapture={() => setEnPause(false)}
    >
      <div
        className="hd-scene"
        role={total > 1 ? 'group' : undefined}
        aria-roledescription={total > 1 ? 'diaporama' : undefined}
        aria-label={total > 1 ? (lang === 'fr' ? 'Photos ABBUSINESS' : 'ABBUSINESS photos') : undefined}
      >
        {photos.map((p, i) => (
          <img
            key={p.fichier}
            src={p.fichier}
            alt={t(p.legende, lang)}
            className={`hd-image${i === courante ? ' hd-image-active' : ''}`}
            /*
             | La premiere image est le plus grand element du premier ecran :
             | la differer degraderait le LCP au lieu de l'ameliorer. Les
             | suivantes attendent.
             */
            loading={i === 0 ? 'eager' : 'lazy'}
            /*
             | fetchpriority en minuscules, et ce n'est pas un detail de
             | style. React 18 ne connait pas la forme camelCase : il la
             | retire de l'element en emettant un avertissement en console,
             | et l'attribut n'arrive jamais dans le HTML. La priorite de
             | chargement etait donc ecrite, lue par les relecteurs, et sans
             | aucun effet. Le test le verifie desormais dans le DOM rendu.
             */
            fetchpriority={i === 0 ? 'high' : 'low'}
            aria-hidden={i !== courante}
            draggable="false"
          />
        ))}
      </div>

      <figcaption className="hd-legende">
        <span className="hd-texte">{t(photo.legende, lang)}</span>

        {total > 1 && (
          <div className="hd-pastilles">
            {photos.map((p, i) => (
              <button
                key={p.fichier}
                type="button"
                className={`hd-pastille${i === courante ? ' hd-pastille-active' : ''}`}
                onClick={() => aller(i)}
                aria-current={i === courante}
                aria-label={
                  lang === 'fr'
                    ? `Photo ${i + 1} sur ${total}`
                    : `Photo ${i + 1} of ${total}`
                }
              />
            ))}
          </div>
        )}
      </figcaption>
    </figure>
  )
}
